/**
 * ISMS Implementation Journey — Visual Flow
 *
 * Displays the full ISMS implementation flow as a horizontally-scrollable
 * diagram with 4 phases, 12 columns, and clickable step boxes that open
 * a guided sidebar panel.
 *
 * Progress is persisted per-tenant in localStorage.
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders } from '../services/api';
import { API_BASE_URL } from '../config';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  FLOW_PHASES,
  FLOW_COLUMNS,
  loadFlowChecks,
  saveFlowChecks,
  loadFlowStepChecks,
  saveFlowStepChecks,
} from '../data/ismsFlowData';
import type { FlowColumn, FlowPhase } from '../data/ismsFlowData';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Column header accent per phase index — real theme tokens, not hardcoded hex,
 *  so the 4-phase colour coding keeps working under dark mode. */
const PHASE_ACCENT = [
  { bg: 'var(--bs-success)', rgb: 'var(--bs-success-rgb)', light: 'var(--bs-success-bg-subtle)' },
  { bg: 'var(--bs-info)',    rgb: 'var(--bs-info-rgb)',    light: 'var(--bs-info-bg-subtle)' },
  { bg: 'var(--bs-primary)', rgb: 'var(--bs-primary-rgb)', light: 'var(--bs-primary-bg-subtle)' },
  { bg: 'var(--bs-warning)', rgb: 'var(--bs-warning-rgb)', light: 'var(--bs-warning-bg-subtle)' },
];
const DEFAULT_ACCENT = { bg: 'var(--bs-secondary)', rgb: 'var(--bs-secondary-rgb)', light: 'var(--np-surface-2)' };

/**
 * Fetches the live ISMS flow config, silently returning undefined on any error
 * so the page always falls back to the bundled defaults without triggering the
 * global auth-logout handler in api.ts.
 */
async function fetchIsmsConfig(): Promise<{ exists: boolean; columns?: FlowColumn[]; phases?: FlowPhase[] } | undefined> {
  try {
    const res = await fetch(`${API_BASE_URL}/config/isms-journey`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (!res.ok) return undefined;
    return res.json();
  } catch {
    return undefined;
  }
}



// ─── Main component ────────────────────────────────────────────────────────

export const IsmsJourneyPage: React.FC = () => {

  // ─── State ───────────────────────────────────────────────────────────────

  const [checks, setChecks] = useState<Set<string>>(() => loadFlowChecks());
  const [stepChecks, setStepChecks] = useState<Record<string, number[]>>(() => loadFlowStepChecks());
  const [panelStepId, setPanelStepId] = useState<string | null>(null);
  const [openPhases, setOpenPhases]   = useState<Set<number>>(new Set([0]));

  // ─── Live config from API (falls back to bundled data on 404 / error) ───────

  const { data: apiConfig } = useQuery({
    queryKey: ['ismsFlowConfig'],
    queryFn:  fetchIsmsConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Merge saved config with bundled defaults: any column in FLOW_COLUMNS that is
  // missing from the saved config (e.g. added after the last admin save) is appended.
  const flowColumns = (() => {
    if (apiConfig?.exists && apiConfig.columns?.length) {
      const savedIds = new Set(apiConfig.columns.map((c: FlowColumn) => c.id));
      return [...apiConfig.columns, ...FLOW_COLUMNS.filter(c => !savedIds.has(c.id))];
    }
    return FLOW_COLUMNS;
  })();
  const flowPhases   = (apiConfig?.exists && apiConfig.phases?.length   ? apiConfig.phases   : null) ?? FLOW_PHASES;
  const allFlowSteps = flowColumns.flatMap(col => col.steps);

  const panelStep = panelStepId ? allFlowSteps.find(s => s.id === panelStepId) ?? null : null;
  const panelCol  = panelStepId ? flowColumns.find(c => c.steps.some(s => s.id === panelStepId)) ?? null : null;

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const toggleCheck = (stepId: string) => {
    setChecks(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId); else next.add(stepId);
      saveFlowChecks(next);
      return next;
    });
  };

  const toggleSubStep = (stepId: string, idx: number) => {
    setStepChecks(prev => {
      const current = new Set<number>(prev[stepId] ?? []);
      if (current.has(idx)) current.delete(idx); else current.add(idx);
      const next = { ...prev, [stepId]: [...current] };
      saveFlowStepChecks(next);
      return next;
    });
  };

  // Auto-mark step complete when all sub-steps ticked
  useEffect(() => {
    allFlowSteps.forEach(step => {
      const total = step.guidance.tips.length;
      if (total === 0) return;
      const done = (stepChecks[step.id] ?? []).length;
      if (done === total && !checks.has(step.id)) {
        setChecks(prev => {
          const next = new Set(prev);
          next.add(step.id);
          saveFlowChecks(next);
          return next;
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepChecks]);

  // Auto-open the first phase that still has incomplete steps (runs once on mount)
  useEffect(() => {
    const firstIncomplete = flowPhases.findIndex((_, phaseIdx) => {
      const steps = flowColumns
        .filter(c => c.phaseIndex === phaseIdx)
        .flatMap(c => c.steps)
        .filter(s => !s.isMilestone);
      return steps.some(s => !checks.has(s.id));
    });
    if (firstIncomplete >= 0) setOpenPhases(new Set([firstIncomplete]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePhase = (idx: number) =>
    setOpenPhases(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });

  // ─── Progress ─────────────────────────────────────────────────────────────

  // Count non-milestone steps only for the overall %
  const nonMilestoneSteps = allFlowSteps.filter(s => !s.isMilestone);
  const doneNonMilestone  = nonMilestoneSteps.filter(s => checks.has(s.id)).length;
  const overallPct = nonMilestoneSteps.length > 0 ? Math.round((doneNonMilestone / nonMilestoneSteps.length) * 100) : 0;

  const phaseProgressArr = flowPhases.map((_phase, phaseIdx) => {
    const cols  = flowColumns.filter(c => c.phaseIndex === phaseIdx);
    const steps = cols.flatMap(c => c.steps).filter(s => !s.isMilestone);
    const done  = steps.filter(s => checks.has(s.id)).length;
    return { done, total: steps.length, pct: steps.length > 0 ? Math.round((done / steps.length) * 100) : 0 };
  });

  // ─── Computed helpers ────────────────────────────────────────────────────

  // Build flat step list grouped by phase → column (for Journey view)
  const phaseStepGroups = (() => {
    let seqNum = 0;
    return flowPhases.map((phase, phaseIdx) => {
      const phaseCols = flowColumns.filter(c => c.phaseIndex === phaseIdx);
      const groups = phaseCols.map(col => ({
        col,
        entries: col.steps.map(step => {
          const num = step.isMilestone ? null : ++seqNum;
          return { step, num };
        }),
      }));
      return { phase, phaseIdx, groups };
    });
  })();

  // First incomplete non-milestone step id across all phases (for "Next" badge)
  const nextStepId = (() => {
    for (const { groups } of phaseStepGroups) {
      for (const { entries } of groups) {
        for (const { step } of entries) {
          if (!step.isMilestone && !checks.has(step.id)) return step.id;
        }
      }
    }
    return null;
  })();

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* ── Page header ── */}
      <PageHeader
        icon="ri-map-2-line"
        title="ISMS Implementation Journey"
        subtitle="Work through each phase in order. Click any step for a how-to guide and tick it off when done."
        actions={
          <NavLink to="/readiness" className="np-btn np-btn-ghost np-btn-sm">
            <i className="ri-clipboard-check-line"></i>Readiness Assessment
          </NavLink>
        }
      />

      {/* ── Overall progress + view toggle ── */}
      <div className="np-card">
        <div className="np-card-body py-3">
          <div className="row align-items-center g-3">
            <div className="col">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-semibold small">Overall Progress</span>
                <div className="d-flex align-items-center gap-2">
                  {overallPct === 100 ? (
                    <span className="np-chip np-tone-success"><i className="ri-shield-check-fill"></i>ISMS Complete</span>
                  ) : overallPct >= 75 ? (
                    <span className="np-chip np-tone-primary"><i className="ri-award-line"></i>Audit Ready</span>
                  ) : overallPct >= 40 ? (
                    <span className="np-chip np-tone-warning"><i className="ri-tools-line"></i>In Progress</span>
                  ) : (
                    <span className="np-chip np-tone-neutral"><i className="ri-time-line"></i>Getting Started</span>
                  )}
                  <span className="np-hint np-tabular mb-0">{doneNonMilestone} / {nonMilestoneSteps.length} steps</span>
                </div>
              </div>
              <div className="np-progress" style={{ height: 8 }}>
                <div
                  className={`np-fill np-tone-${overallPct >= 75 ? 'success' : overallPct >= 40 ? 'primary' : 'warning'}`}
                  style={{ width: `${overallPct}%`, transition: 'width 0.5s ease' }}
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          JOURNEY VIEW — vertical accordion phases with sequential step list
         ════════════════════════════════════════════════════════════════════ */}
      <div className="mb-4">
          {phaseStepGroups.map(({ phase, phaseIdx, groups }) => {
            const pp           = phaseProgressArr[phaseIdx];
            const accent       = PHASE_ACCENT[phaseIdx] ?? DEFAULT_ACCENT;
            const phaseBg      = accent.bg;
            const phaseLightBg = accent.light;
            const isOpen       = openPhases.has(phaseIdx);
            const isComplete   = pp.total > 0 && pp.done === pp.total;
            const isCurrentPhase = groups
              .flatMap(g => g.entries)
              .some(({ step }) => step.id === nextStepId);

            return (
              <div
                key={phase.id}
                className="np-card mb-3"
                style={{
                  border: `1.5px solid ${isOpen ? `rgba(${accent.rgb}, 0.6)` : 'var(--np-border)'}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}
              >
                {/* ── Phase accordion header ── */}
                <div
                  className="d-flex align-items-center gap-3 px-4 py-3"
                  style={{
                    backgroundColor: isOpen ? phaseBg : 'var(--np-surface)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    borderBottom: isOpen ? `1px solid rgba(${accent.rgb}, 0.4)` : 'none',
                    userSelect: 'none',
                  }}
                  onClick={() => togglePhase(phaseIdx)}
                >
                  {/* Phase number circle */}
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                    style={{
                      width: 38, height: 38,
                      backgroundColor: isOpen ? 'rgba(255,255,255,0.2)' : phaseBg,
                      border: `2px solid ${isOpen ? 'rgba(255,255,255,0.4)' : phaseBg}`,
                      color: 'var(--bs-white)',
                      fontSize: '0.9rem',
                    }}
                  >
                    {isComplete ? <i className="ri-check-line" /> : <span>{phaseIdx + 1}</span>}
                  </div>

                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="fw-bold"
                        style={{ fontSize: '0.95rem', color: isOpen ? 'var(--bs-white)' : 'var(--bs-heading-color)' }}
                      >
                        Phase {phaseIdx + 1}: {phase.label}
                      </span>
                      {isCurrentPhase && !isOpen && (
                        <span className="np-chip" style={{ background: `rgba(${accent.rgb}, 0.14)`, color: phaseBg, border: `1px solid rgba(${accent.rgb}, 0.4)` }}>
                          <i className="ri-play-circle-line"></i>Current phase
                        </span>
                      )}
                      {isComplete && (
                        isOpen ? (
                          <span className="np-chip" style={{ background: 'var(--bs-white)', color: 'var(--bs-success)' }}>
                            <i className="ri-check-double-line"></i>Complete
                          </span>
                        ) : (
                          <span className="np-chip np-tone-success">
                            <i className="ri-check-double-line"></i>Complete
                          </span>
                        )
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <div
                        className="np-progress flex-grow-1"
                        style={{ height: 5, backgroundColor: isOpen ? 'rgba(255,255,255,0.25)' : 'var(--np-surface-2)', maxWidth: 220 }}
                      >
                        <div
                          className="np-fill"
                          style={{
                            width: `${pp.pct}%`,
                            backgroundColor: isOpen ? 'var(--bs-white)' : (isComplete ? 'var(--bs-success)' : phaseBg),
                            transition: 'width 0.4s ease',
                          }}
                        />
                      </div>
                      <span className="np-tabular" style={{ fontSize: '0.72rem', color: isOpen ? 'rgba(255,255,255,0.8)' : 'var(--bs-secondary-color)', whiteSpace: 'nowrap' }}>
                        {pp.done}/{pp.total} steps
                      </span>
                    </div>
                  </div>

                  <i
                    className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line flex-shrink-0`}
                    style={{ fontSize: '1.25rem', color: isOpen ? 'rgba(255,255,255,0.8)' : 'var(--bs-secondary-color)' }}
                  />
                </div>

                {/* ── Phase body — step list grouped by column ── */}
                {isOpen && (
                  <div className="px-4 py-4" style={{ backgroundColor: 'var(--np-surface-2)' }}>
                    {groups.map(({ col, entries }, colIdx) => (
                      <div key={col.id} style={{ marginBottom: colIdx < groups.length - 1 ? 28 : 0 }}>

                        {/* Column label */}
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: phaseBg, flexShrink: 0 }} />
                          <i className={col.icon} style={{ color: phaseBg, fontSize: '0.9rem' }} />
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: phaseBg, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {col.label}
                          </span>
                        </div>

                        {/* Step rows */}
                        <div style={{ paddingLeft: 6 }}>
                          {entries.map(({ step, num }, entryIdx) => {
                            const isDone       = checks.has(step.id);
                            const isNext       = step.id === nextStepId;
                            const hasUnmetDeps = (step.dependsOn ?? []).some(id => !checks.has(id));
                            const tipsTotal    = step.guidance.tips.length;
                            const tipsDone     = (stepChecks[step.id] ?? []).length;
                            const isLastEntry  = entryIdx === entries.length - 1;

                            // ── Milestone banner ──
                            if (step.isMilestone) {
                              const mBg = step.milestoneColour === 'danger' ? 'var(--bs-danger)' : phaseBg;
                              return (
                                <div
                                  key={step.id}
                                  className="d-flex align-items-center gap-2 rounded px-3 py-2 my-3"
                                  style={{
                                    backgroundColor: isDone ? 'var(--bs-success)' : mBg,
                                    color: 'var(--bs-white)',
                                    cursor: 'pointer',
                                    fontSize: '0.82rem',
                                    fontWeight: 600,
                                  }}
                                  onClick={() => setPanelStepId(step.id)}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input flex-shrink-0"
                                    checked={isDone}
                                    style={{ filter: 'brightness(0) invert(1)', width: 14, height: 14, marginTop: 0, cursor: 'pointer' }}
                                    onChange={e => { e.stopPropagation(); toggleCheck(step.id); }}
                                    onClick={e => e.stopPropagation()}
                                  />
                                  <i className="ri-trophy-line flex-shrink-0" />
                                  <span className="flex-grow-1">{step.label}</span>
                                  <i className="ri-information-line flex-shrink-0 opacity-75" style={{ fontSize: '0.9rem' }} />
                                </div>
                              );
                            }

                            // ── Regular step row ──
                            return (
                              <div
                                key={step.id}
                                className="d-flex align-items-start gap-3"
                                style={{ position: 'relative', marginBottom: 4 }}
                              >
                                {/* Vertical connector line between steps */}
                                {!isLastEntry && (
                                  <div style={{
                                    position: 'absolute',
                                    left: 14,
                                    top: 36,
                                    bottom: -4,
                                    width: 2,
                                    backgroundColor: isDone ? `rgba(${accent.rgb}, 0.6)` : 'var(--np-border)',
                                    zIndex: 0,
                                  }} />
                                )}

                                {/* Sequence number circle */}
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                                  style={{
                                    width: 30, height: 30, minWidth: 30, marginTop: 8, zIndex: 1,
                                    backgroundColor: isDone ? phaseBg : isNext ? `rgba(${accent.rgb}, 0.2)` : 'var(--np-surface-2)',
                                    border: `2px solid ${isDone ? phaseBg : isNext ? phaseBg : 'var(--np-border)'}`,
                                    color: isDone ? 'var(--bs-white)' : isNext ? phaseBg : 'var(--bs-secondary-color)',
                                    fontSize: '0.7rem',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  {isDone ? <i className="ri-check-line" /> : num}
                                </div>

                                {/* Step card */}
                                <div
                                  className="flex-grow-1 rounded px-3 py-2"
                                  style={{
                                    backgroundColor: isDone ? phaseLightBg : 'var(--np-surface)',
                                    border: `1px solid ${isDone ? `rgba(${accent.rgb}, 0.4)` : isNext ? phaseBg : 'var(--np-border)'}`,
                                    cursor: 'pointer',
                                    boxShadow: isNext ? `0 0 0 3px rgba(${accent.rgb}, 0.2)` : 'none',
                                    transition: 'border-color 0.15s, box-shadow 0.15s',
                                  }}
                                  onClick={() => setPanelStepId(step.id)}
                                >
                                  <div className="d-flex align-items-start gap-2">
                                    <input
                                      type="checkbox"
                                      className="form-check-input flex-shrink-0"
                                      checked={isDone}
                                      style={{ cursor: 'pointer', marginTop: 3, width: 14, height: 14 }}
                                      onChange={e => { e.stopPropagation(); toggleCheck(step.id); }}
                                      onClick={e => e.stopPropagation()}
                                    />
                                    <div className="flex-grow-1 min-w-0">
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span
                                          style={{
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            color: isDone ? 'var(--bs-secondary-color)' : 'var(--bs-heading-color)',
                                            textDecoration: isDone ? 'line-through' : 'none',
                                            textDecorationColor: 'var(--np-border-strong)',
                                          }}
                                        >
                                          {step.label}
                                        </span>
                                        {isNext && (
                                          <span className="np-chip" style={{ backgroundColor: phaseBg, color: 'var(--bs-white)' }}>
                                            Next
                                          </span>
                                        )}
                                        {hasUnmetDeps && (
                                          <span className="np-chip np-tone-warning">
                                            <i className="ri-links-line"></i>Prerequisites incomplete
                                          </span>
                                        )}
                                      </div>
                                      {tipsTotal > 0 && (
                                        <div className="np-tabular" style={{ fontSize: '0.72rem', color: isDone ? phaseBg : 'var(--bs-secondary-color)', marginTop: 2 }}>
                                          {tipsDone}/{tipsTotal} sub-steps done
                                        </div>
                                      )}
                                    </div>
                                    <i className="ri-information-line flex-shrink-0 np-hint" style={{ fontSize: '0.9rem', marginTop: 2 }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* ── Offcanvas (portalled to body so it covers navbar + banner) ── */}
      {createPortal(
        <>
          {panelStepId && (
            <div className="offcanvas-backdrop fade show" onClick={() => setPanelStepId(null)} />
          )}
          <div
            className={`offcanvas offcanvas-end${panelStepId ? ' show' : ''}`}
            style={{ width: 480, visibility: panelStepId ? 'visible' : 'hidden' }}
            tabIndex={-1}
          >
        {panelStep && panelCol && (() => {
          const phaseIdx  = panelCol.phaseIndex;
          const phase     = flowPhases[phaseIdx];
          const panelAccent = PHASE_ACCENT[phaseIdx] ?? DEFAULT_ACCENT;
          const headerBg  = panelAccent.bg;
          const lightBg   = panelAccent.light;
          const isDone    = checks.has(panelStep.id);
          const tipsChecked = stepChecks[panelStep.id] ?? [];

          return (
            <>
              {/* Header */}
              <div
                className="offcanvas-header border-bottom"
                style={{ backgroundColor: lightBg, padding: '12px 16px', borderColor: `rgba(${panelAccent.rgb}, 0.3)` }}
              >
                <div className="flex-grow-1 me-3">
                  <div className="d-flex align-items-center gap-2 mb-1 np-hint" style={{ fontSize: '0.7rem' }}>
                    <span
                      className="np-chip"
                      style={{ backgroundColor: headerBg, color: 'var(--bs-white)' }}
                    >
                      Phase {phaseIdx + 1}
                    </span>
                    <span>{phase.label}</span>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <i className={panelCol.icon} style={{ color: headerBg }}></i>
                    <span style={{ color: headerBg, fontWeight: 600 }}>{panelCol.label}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i className={`${panelStep.icon} flex-shrink-0`} style={{ color: headerBg, fontSize: '1.1rem' }}></i>
                    <h6 className="offcanvas-title fw-bold mb-0" style={{ fontSize: '0.95rem' }}>{panelStep.label}</h6>
                    {isDone && (
                      <span className="np-chip np-tone-success">
                        <i className="ri-check-line"></i>Complete
                      </span>
                    )}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setPanelStepId(null)} />
              </div>

              {/* Body */}
              <div className="offcanvas-body p-0" style={{ overflowY: 'auto' }}>

                {/* Prerequisites */}
                {panelStep.dependsOn && panelStep.dependsOn.length > 0 && (
                  <div className="p-3 border-bottom">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="ri-links-line" style={{ color: headerBg, fontSize: '0.9rem' }} />
                      <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>Prerequisites</span>
                    </div>
                    <p className="np-hint mb-2" style={{ fontSize: '0.75rem', lineHeight: 1.5 }}>These steps should ideally be completed before this one:</p>
                    {panelStep.dependsOn.map(depId => {
                      const depStep = allFlowSteps.find(s => s.id === depId);
                      const depDone = checks.has(depId);
                      return (
                        <div
                          key={depId}
                          className="d-flex align-items-center gap-2 p-2 mb-1 rounded"
                          style={{
                            backgroundColor: depDone ? 'var(--bs-success-bg-subtle)' : 'var(--bs-danger-bg-subtle)',
                            border: `1px solid ${depDone ? 'var(--bs-success-border-subtle)' : 'var(--bs-danger-border-subtle)'}`,
                            cursor: depStep ? 'pointer' : 'default',
                          }}
                          onClick={() => depStep && setPanelStepId(depId)}
                        >
                          <i
                            className={depDone ? 'ri-checkbox-circle-fill text-success' : 'ri-error-warning-line text-danger'}
                            style={{ fontSize: '0.9rem', flexShrink: 0 }}
                          />
                          <span className="flex-grow-1" style={{ fontSize: '0.8rem' }}>{depStep?.label ?? depId}</span>
                          <span className={`np-chip np-tone-${depDone ? 'success' : 'danger'}`}>
                            {depDone ? 'Done' : 'Needed'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Intro */}
                <div className="p-3 border-bottom">
                  <div
                    className="mb-0 text-body"
                    style={{ fontSize: '0.875rem', lineHeight: 1.8 }}
                    dangerouslySetInnerHTML={{ __html: panelStep.guidance.intro }}
                  />
                </div>
                {/* Sub-steps */}
                {panelStep.guidance.tips.length > 0 && (
                  <div>
                    <div
                      className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                      style={{ backgroundColor: 'var(--bs-body-bg)' }}
                    >
                      <span className="fw-semibold" style={{ fontSize: '0.8rem' }}>
                        <i className="ri-list-check-2 me-1"></i>Steps to complete
                      </span>
                      <span className="np-hint np-tabular">
                        {tipsChecked.length} / {panelStep.guidance.tips.length} done
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="np-progress" style={{ height: 3, borderRadius: 0 }}>
                      <div
                        className="np-fill"
                        style={{
                          width: `${Math.round((tipsChecked.length / panelStep.guidance.tips.length) * 100)}%`,
                          backgroundColor: tipsChecked.length === panelStep.guidance.tips.length ? 'var(--bs-success)' : headerBg,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    {panelStep.guidance.tips.map((tip, idx) => {
                      const checked = tipsChecked.includes(idx);
                      return (
                        <div
                          key={idx}
                          className={`d-flex align-items-start gap-3 px-3 py-3 border-bottom${checked ? ' bg-body-secondary' : ''}`}
                          style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                          onClick={() => toggleSubStep(panelStep.id, idx)}
                        >
                          <div
                            className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle fw-bold"
                            style={{
                              width: 26, height: 26, fontSize: '0.72rem', marginTop: 1,
                              backgroundColor: checked ? 'var(--bs-success)' : `rgba(${panelAccent.rgb}, 0.13)`,
                              color: checked ? 'var(--bs-white)' : headerBg,
                            }}
                          >
                            {checked
                              ? <i className="ri-check-line" style={{ fontSize: '0.8rem' }}></i>
                              : idx + 1
                            }
                          </div>
                          <p
                            className={`mb-0 flex-grow-1 ${checked ? 'np-hint' : 'text-body'}`}
                            style={{
                              fontSize: '0.85rem',
                              lineHeight: 1.65,
                              textDecoration: checked ? 'line-through' : 'none',
                              textDecorationColor: 'var(--np-border-strong)',
                            }}
                          >
                            {tip}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Done when */}
                <div className="p-3">
                  <div
                    className="d-flex align-items-start gap-2 p-3 rounded"
                    style={{ backgroundColor: 'var(--bs-success-bg-subtle)', border: '1px solid var(--bs-success-border-subtle)' }}
                  >
                    <i className="ri-checkbox-circle-line text-success flex-shrink-0 mt-1"></i>
                    <div>
                      <span className="fw-semibold text-success d-block mb-1" style={{ fontSize: '0.8rem' }}>You're done when:</span>
                      <div
                        className="np-hint"
                        style={{ fontSize: '0.825rem', lineHeight: 1.65 }}
                        dangerouslySetInnerHTML={{ __html: panelStep.guidance.doneWhen }}
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="border-top p-3 d-flex align-items-center gap-2">
                <button
                  className="np-btn flex-grow-1 justify-content-center"
                  style={{
                    backgroundColor: isDone ? 'transparent' : headerBg,
                    color: isDone ? 'var(--bs-secondary-color)' : 'var(--bs-white)',
                    border: isDone ? '1px solid var(--np-border-strong)' : `1px solid ${headerBg}`,
                  }}
                  onClick={() => toggleCheck(panelStep.id)}
                >
                  {isDone ? (
                    <><i className="ri-close-circle-line"></i>Mark as incomplete</>
                  ) : (
                    <><i className="ri-checkbox-circle-line"></i>Mark as complete</>
                  )}
                </button>
                {panelStep.path && (
                  <NavLink
                    to={(() => {
                      // Accept full URLs (https://…/app/#/people) or bare paths (/people).
                      // NavLink needs just the route portion, e.g. /people.
                      const p = panelStep.path!;
                      const hashIdx = p.indexOf('#/');
                      return hashIdx !== -1 ? p.slice(hashIdx + 1) : p;
                    })()}
                    className="np-btn np-btn-ghost flex-shrink-0"
                  >
                    {panelStep.actionLabel ?? 'Go there'} <i className="ri-arrow-right-line"></i>
                  </NavLink>
                )}
              </div>
            </>
          );
        })()}
          </div>
        </>,
        document.body,
      )}

    </div>
  );
};
